import type { ReactNode } from 'react';
import { useBrandingStore } from '@/stores/branding-store';

/**
 * Split-screen auth layout inspired by the Q PaymentZ reference design.
 * Single seamless gradient covers the entire screen (no color separation).
 * Left: animated SVG icon with floating effects.
 * Right: auth form content (login / signup).
 * On mobile the form stacks on top with a compact animated banner below.
 */
export function AuthLayout({ children }: { children: ReactNode }) {
  const { config: branding } = useBrandingStore();
  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-blue-100/70 via-sky-50 to-teal-100/60">
      {/* Soft gradient orbs spread across entire screen for seamless look */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-[500px] w-[500px] rounded-full bg-blue-200/30 blur-[120px]" />
      <div className="pointer-events-none absolute right-1/4 top-0 h-[400px] w-[400px] rounded-full bg-purple-100/25 blur-[100px]" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-[450px] w-[450px] rounded-full bg-teal-200/30 blur-[110px]" />
      <div className="pointer-events-none absolute -bottom-20 -right-20 h-[400px] w-[400px] rounded-full bg-sky-100/30 blur-[100px]" />

      {/* Layout container */}
      <div className="relative flex min-h-screen flex-col-reverse lg:flex-row">
        {/* ── Left: Animated visual panel ── */}
        <div className="flex flex-1 items-center justify-center p-8 lg:p-12">
          {/* Icon + orbiting elements container */}
          <div className="relative flex flex-col items-center gap-8">
            {/* Orbiting dots – hidden on mobile for perf */}
            <div className="pointer-events-none absolute hidden lg:block">
              <span
                className="auth-orbit-dot absolute h-2.5 w-2.5 rounded-full bg-orange-400 shadow-lg shadow-orange-400/40"
                style={{ top: '-80px', left: '-60px' }}
              />
              <span
                className="auth-orbit-dot-reverse absolute h-2 w-2 rounded-full bg-purple-400 shadow-lg shadow-purple-400/40"
                style={{ top: '-50px', right: '-70px' }}
              />
              <span
                className="auth-orbit-dot absolute h-1.5 w-1.5 rounded-full bg-teal-400 shadow-lg shadow-teal-400/40"
                style={{ bottom: '-40px', left: '30px' }}
              />
            </div>

            {/* Q PaymentZ SVG Icon */}
            <div className="auth-float relative">
              {/* Icon glow */}
              <div className="auth-glow absolute inset-0 rounded-full bg-teal-500/20 blur-2xl" />

              {/* SVG icon – bigger size */}
              <img
                src="/qpaymentz_icon.svg"
                alt="Q PaymentZ"
                className="relative h-[250px] w-[250px] drop-shadow-2xl lg:h-[380px] lg:w-[380px]"
              />
            </div>

            {/* Branding text */}
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-800 lg:text-3xl">{branding.businessTitle || 'Q PaymentZ'}</h2>
              <p className="mt-1 text-sm text-gray-500 lg:text-base">
                {branding.businessDescription || 'Secure, Send & Receive Payments.'}
              </p>
            </div>
          </div>
        </div>

        {/* ── Right: Auth form section ── */}
        <div className="flex flex-1 items-center justify-center p-6 lg:p-12">{children}</div>
      </div>
    </div>
  );
}
